import React, { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PageHeader, StatusBadge, EmptyState, money } from "@/components/shared";
import LineItemsEditor from "@/components/LineItemsEditor";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Handshake, Plus, Trash2, ArrowRight, Hammer, PackageOpen, FileText } from "lucide-react";
import RawProcessing from "@/pages/RawProcessing";

const EXT_CATS = [
  { v: "raw_material", l: "Raw Material" },
  { v: "store_material", l: "Store Material" },
];
const STORE_PARTIES = ["Raw Wood Store", "Hardware Store", "Polish Store", "Packing Store", "Tools & Machinery Store", "Other Misc Store"];
const CAT_L = { raw_material: "Raw Material", store_material: "Store Material", contract_work: "Contract Work" };
const NEXT = { draft: "issued", issued: "completed", completed: "draft" };
const empty = {
  order_type: "external", buyer_id: "", sku: "", buyer_po_no: "", party: "", category: "raw_material", product: "",
  supplier_gst: "", supplier_address: "", supplier_contact: "",
  order_date: "", expected_date: "", status: "draft", notes: "", items: [], work_type: "",
};
const SECTION = { raw_wood: "wood_lines", polish: "polish_lines", packing: "packing_lines", hardware: "hardware_lines" };

export default function SalesOrders() {
  const { user } = useAuth();
  const canManage = ["admin", "manager"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [products, setProducts] = useState([]);
  const [costings, setCostings] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState("all");
  const [view, setView] = useState("orders");

  const load = () => api.get("/sales-orders").then((r) => setItems(r.data));
  const downloadSoPdf = async (o) => {
    try {
      const res = await api.get(`/sales-orders/${o.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${o.so_number || "order"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate PDF"); }
  };
  const downloadGumming = async (o) => {
    try {
      const res = await api.get(`/sales-orders/${o.id}/gumming.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${o.so_number || "order"}_gumming.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("No serials on this order yet"); }
  };
  useEffect(() => {
    load();
    api.get("/buyers").then((r) => setBuyers(r.data));
    api.get("/ledgers").then((r) => setLedgers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/costings").then((r) => setCostings(r.data || []));
  }, []);

  const skuOptions = useMemo(() => {
    const m = new Map();
    products.forEach((p) => p.sku && m.set(p.sku, p.name));
    costings.forEach((c) => c.sku && !m.has(c.sku) && m.set(c.sku, c.product_name));
    return [...m.entries()].map(([sku, name]) => ({ sku, name }));
  }, [products, costings]);

  const partyLedgers = ledgers.filter((l) => (l.type || "") === (form.order_type === "internal" ? "contractor" : "vendor"));

  const fillFromSku = async (sku, wt) => {
    if (!sku || !wt) return;
    try {
      const { data } = await api.get(`/costings/by-sku/${encodeURIComponent(sku)}`);
      const lines = data[SECTION[wt]] || [];
      const items = lines.map((l) => {
        if (wt === "raw_wood") {
          const bf = (Number(l.l_ft) || 0) * (Number(l.w_in) || 0) * (Number(l.t_in) || 0) / 12 * (Number(l.qty) || 0);
          return { name: l.part || l.material || "Wood", qty: Number(bf.toFixed(3)) || (Number(l.qty) || 1), unit: "bd-ft", rate: Number(l.rate) || 0 };
        }
        return { name: l.item_name || "", qty: Number(l.qty) || 1, unit: "", rate: Number(l.rate) || 0 };
      });
      setForm((f) => ({ ...f, items }));
      toast.success(items.length ? `${items.length} line(s) loaded from ${sku}` : `No ${wt} lines on ${sku}'s cost sheet`);
    } catch (e) { toast.error("No cost sheet found for this SKU"); }
  };

  const setType = (t) =>
    setForm((f) => ({ ...f, order_type: t, category: t === "internal" ? "contract_work" : "raw_material" }));

  const create = async () => {
    try {
      const b = buyers.find((x) => x.id === form.buyer_id);
      const p = products.find((x) => x.sku === form.sku);
      const isContractor = form.order_type === "internal" && !STORE_PARTIES.includes(form.party) && !!form.work_type;
      await api.post("/sales-orders", {
        ...form,
        buyer_name: b?.name || "",
        product_name: p?.name || form.product,
        items: form.items.map((i) => ({ ...i, qty: Number(i.qty), rate: Number(i.rate) })),
      });
      toast.success(isContractor ? "Order created — allotment queue added in Stores" : "Sales order created");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const advance = async (o) => {
    const to = NEXT[o.status];
    const { data } = await api.patch(`/sales-orders/${o.id}/status`, { status: to });
    setItems((prev) => prev.map((x) => (x.id === o.id ? data : x)));
    if (o.status === "completed") toast.info("Order reopened — issued stock and production stage rolled back");
    else if (to === "completed") toast.success("Order completed — stock deducted & stage updated");
  };
  const remove = async (id) => { await api.delete(`/sales-orders/${id}`); toast.success("Deleted"); load(); };

  const shown = tab === "all" ? items : items.filter((i) => i.order_type === tab);
  const isInternal = form.order_type === "internal";

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-2">
        {[["orders", "Sales Orders"], ["raw", "Raw Processing"]].map(([v, l]) => (
          <button key={v} data-testid={`so-view-${v}`} onClick={() => { setView(v); if (v === "orders") load(); }}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors duration-200 ${view === v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40"}`}>{l}</button>
        ))}
      </div>

      {view === "raw" ? <RawProcessing /> : (<>
      <PageHeader title="Sales Orders" icon={Handshake}
        subtitle="Internal orders to contract workers and external orders for raw & store material purchasing."
        action={canManage && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (v) setForm(empty); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-so-button" className="gap-2 transition-transform duration-200 hover:-translate-y-px">
                <Plus className="h-4 w-4" /> New Sales Order
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-heading">New Sales Order</DialogTitle></DialogHeader>
              <div className="max-h-[72vh] space-y-4 overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-2">
                  <button type="button" data-testid="so-type-external" onClick={() => setType("external")}
                    className={`flex items-center gap-3 rounded-md border p-3 text-left transition-colors duration-200 ${!isInternal ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
                    <PackageOpen className={`h-5 w-5 ${!isInternal ? "text-primary" : "text-muted-foreground"}`} />
                    <div><p className="text-sm font-semibold">External</p><p className="text-xs text-muted-foreground">Purchase raw / store material</p></div>
                  </button>
                  <button type="button" data-testid="so-type-internal" onClick={() => setType("internal")}
                    className={`flex items-center gap-3 rounded-md border p-3 text-left transition-colors duration-200 ${isInternal ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}>
                    <Hammer className={`h-5 w-5 ${isInternal ? "text-primary" : "text-muted-foreground"}`} />
                    <div><p className="text-sm font-semibold">Internal</p><p className="text-xs text-muted-foreground">Order to contract worker</p></div>
                  </button>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2"><Label>Buyer</Label>
                    <Select value={form.buyer_id} onValueChange={(v) => setForm({ ...form, buyer_id: v })}>
                      <SelectTrigger data-testid="so-buyer"><SelectValue placeholder="Select buyer" /></SelectTrigger>
                      <SelectContent>{buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label>SKU / Product (required)</Label>
                    <Select value={form.sku} onValueChange={(v) => { setForm({ ...form, sku: v }); if (isInternal && form.work_type) fillFromSku(v, form.work_type); }}>
                      <SelectTrigger data-testid="so-sku"><SelectValue placeholder="Select SKU" /></SelectTrigger>
                      <SelectContent>
                        {skuOptions.map((o) => <SelectItem key={o.sku} value={o.sku}>{o.sku} · {o.name}</SelectItem>)}
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-2">
                    <Label>{isInternal ? "Store / Contract worker" : "Supplier / Vendor"}</Label>
                    <Select value={form.party || ""}
                      onValueChange={(v) => {
                        const l = ledgers.find((x) => x.name === v);
                        setForm((f) => ({ ...f, party: v,
                          supplier_gst: (!isInternal && l) ? (l.gst || "") : f.supplier_gst,
                          supplier_address: (!isInternal && l) ? (l.address || "") : f.supplier_address,
                          supplier_contact: (!isInternal && l) ? (l.contact || "") : f.supplier_contact }));
                      }}>
                      <SelectTrigger data-testid="so-party-select"><SelectValue placeholder={isInternal ? "Select store or contractor" : "Select vendor from ledgers"} /></SelectTrigger>
                      <SelectContent>
                        {isInternal && STORE_PARTIES.map((s) => <SelectItem key={s} value={s}>{s} (auto-deducts stock)</SelectItem>)}
                        {partyLedgers.map((l) => <SelectItem key={l.id} value={l.name}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input data-testid="so-party" value={form.party}
                      onChange={(e) => setForm((f) => ({ ...f, party: e.target.value,
                        supplier_gst: "", supplier_address: "", supplier_contact: "" }))}
                      placeholder="or type a name" />
                  </div>
                  {isInternal && (
                    <div className="space-y-2"><Label>Work type (auto-fills rates from SKU)</Label>
                      <Select value={form.work_type || ""} onValueChange={(v) => { setForm((f) => ({ ...f, work_type: v })); fillFromSku(form.sku, v); }}>
                        <SelectTrigger data-testid="so-work-type"><SelectValue placeholder="Raw wood / Polish / Packing / Hardware" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="raw_wood">Raw wood</SelectItem>
                          <SelectItem value="polish">Polish</SelectItem>
                          <SelectItem value="packing">Packing</SelectItem>
                          <SelectItem value="hardware">Hardware</SelectItem>
                        </SelectContent>
                      </Select></div>
                  )}
                  <div className="space-y-2"><Label>Buyer PO No</Label>
                    <Input data-testid="so-buyer-po" value={form.buyer_po_no} onChange={(e) => setForm({ ...form, buyer_po_no: e.target.value })} placeholder="CB-2026-5567" /></div>
                  {isInternal && (
                    <div className="space-y-2 sm:col-span-2"><Label>Work item / note</Label>
                      <Input data-testid="so-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="Dining Table – polishing" /></div>
                  )}
                  <div className="space-y-2"><Label>Order date</Label>
                    <Input type="date" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} /></div>
                  <div className="space-y-2"><Label>Expected date</Label>
                    <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} /></div>
                </div>

                {!isInternal && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="space-y-2"><Label>Material type</Label>
                      <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                        <SelectTrigger data-testid="so-category"><SelectValue /></SelectTrigger>
                        <SelectContent>{EXT_CATS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                      </Select></div>
                    <div className="space-y-2"><Label>Supplier GST</Label>
                      <Input data-testid="so-gst" value={form.supplier_gst} onChange={(e) => setForm({ ...form, supplier_gst: e.target.value })} /></div>
                    <div className="space-y-2"><Label>Supplier contact</Label>
                      <Input data-testid="so-contact" value={form.supplier_contact} onChange={(e) => setForm({ ...form, supplier_contact: e.target.value })} /></div>
                    <div className="space-y-2 sm:col-span-3"><Label>Supplier address</Label>
                      <Input data-testid="so-address" value={form.supplier_address} onChange={(e) => setForm({ ...form, supplier_address: e.target.value })} /></div>
                  </div>
                )}

                <LineItemsEditor label={isInternal ? "Products / SKUs to work on" : "Material Items"} testid="so-items" showUnit={false}
                  skuOptions={isInternal ? skuOptions.map((o) => ({ sku: o.sku, label: `${o.sku} · ${o.name}` })) : undefined}
                  items={form.items} setItems={(v) => setForm({ ...form, items: v })} />

                <div className="space-y-2"><Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Terms, instructions…" /></div>
              </div>
              <DialogFooter><Button data-testid="so-save" onClick={create} disabled={!form.party || !form.sku || (!isInternal && !form.buyer_id)}>Create Order</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )} />

      <div className="flex flex-wrap gap-2">
        {[["all", "All"], ["external", "External · Material"], ["internal", "Internal · Contractors"]].map(([v, l]) => (
          <button key={v} data-testid={`so-tab-${v}`} onClick={() => setTab(v)}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-200 ${tab === v ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground hover:border-primary/40"}`}>{l}</button>
        ))}
      </div>

      {shown.length === 0 ? (
        <EmptyState icon={Handshake} title="No sales orders yet" hint="Create an internal (contractor) or external (material) order." />
      ) : (
        <Card className="overflow-hidden rounded-md border-border shadow-none">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Order #</TableHead><TableHead>Type</TableHead><TableHead>Party</TableHead>
                <TableHead>Detail</TableHead><TableHead className="text-right">Value</TableHead>
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((o) => (
                <TableRow key={o.id} data-testid={`so-row-${o.id}`} className="transition-colors duration-200 hover:bg-muted/40">
                  <TableCell className="font-semibold">{o.so_number}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`rounded-full text-[11px] font-semibold ${o.order_type === "internal" ? "border-ochre/30 bg-ochre/10 text-ochre" : "border-primary/30 bg-primary/10 text-primary"}`}>
                      {o.order_type === "internal" ? "Internal" : "External"}
                    </Badge>
                  </TableCell>
                  <TableCell>{o.party}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {o.order_type === "internal" ? (o.product || "Contract work") : (o.product || o.product_name || CAT_L[o.category])}
                    {o.stock_item_id && <span className="ml-1 text-[10px] font-semibold uppercase text-ochre">· reorder</span>}
                    <span className="ml-1 text-xs">· {o.items?.length || 0} items</span>
                  </TableCell>
                  <TableCell className="text-right font-heading font-bold">{money(o.total)}</TableCell>
                  <TableCell><StatusBadge status={o.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" data-testid={`so-pdf-${o.id}`} onClick={() => downloadSoPdf(o)}
                        className="h-8 w-8 text-muted-foreground hover:text-primary"><FileText className="h-4 w-4" /></Button>
                      {o.order_type === "internal" && (
                        <Button variant="ghost" size="sm" data-testid={`so-gumming-${o.id}`} onClick={() => downloadGumming(o)}
                          className="h-8 gap-1 text-xs" title="Serial / gumming labels">Serials</Button>
                      )}
                      <Button variant="ghost" size="sm" data-testid={`so-advance-${o.id}`} onClick={() => advance(o)} className="h-8 gap-1 text-xs">
                        <ArrowRight className="h-3.5 w-3.5" /> {o.status === "completed" ? "Reopen" : NEXT[o.status]}
                      </Button>
                      {canManage && (
                        <Button variant="ghost" size="icon" data-testid={`so-delete-${o.id}`} onClick={() => remove(o.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      </>)}
    </div>
  );
}
